import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Fingerprint, Home, RefreshCw, ShieldAlert, UserRound } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/page-header';
import { RouteBreadcrumb } from '@/components/route-breadcrumb';
import { HomeownerPoolDetails } from '@/components/admin/homeowner-pool-details';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Textarea } from '@/components/ui/textarea';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import {
  approveRecoveryCase,
  cancelRecoveryCase,
  executeRecoveryCase,
  fetchRecoveryCase,
  rejectRecoveryCase,
  revokeRecoveryCaseApproval,
  selectRecoveryApprovalRevoking,
  selectRecoveryCancelling,
  selectRecoveryCaseError,
  selectRecoveryCaseLoading,
  selectRecoveryDecisionSubmitting,
  selectRecoveryExecuting,
  selectRecoveryExecutionFeedback,
  selectSelectedRecoveryCase,
  selectSelectedRecoveryEvidence,
  clearSelectedRecoveryCase,
  type RecoveryCaseDisplayStatus,
} from '@/features/recovery';
import {
  clearHomeownerUsersPool,
  findHomeownerPoolUserBySub,
  selectExactHomeownerPoolUser,
  selectHomeownerPoolError,
  selectHomeownerPoolErrorKind,
  selectHomeownerPoolLoading,
} from '@/features/homeowner-users-pool';
import { formatUnixDateTimeCompact } from '@/lib';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

const STATUS_VARIANTS: Record<RecoveryCaseDisplayStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  EVIDENCE_VERIFIED: 'outline',
  PENDING_REVIEW: 'secondary',
  APPROVED: 'default',
  EXECUTING: 'secondary',
  OPERATOR_REVIEW_REQUIRED: 'destructive',
  RESERVATION_RELEASE_PENDING: 'secondary',
  COMPLETED: 'default',
  REJECTED: 'outline',
  CANCELLED: 'outline',
  FAILED: 'destructive',
  UNKNOWN: 'destructive',
};

function toLabel(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function statusDescription(status: RecoveryCaseDisplayStatus, canRetryCompletion = false, canCancel = false): string {
  switch (status) {
    case 'EVIDENCE_VERIFIED':
      return 'The claimant has not submitted this case for review yet.';
    case 'PENDING_REVIEW':
      return 'Review the submitted evidence and HomeVein account context before making a decision.';
    case 'APPROVED':
      return 'Evidence approval is complete. Before execution, you may revoke approval or cancel the request when the API permits it.';
    case 'EXECUTING':
      return canRetryCompletion
        ? 'The approved operation is unresolved. Reload the case and retry completion of that same operation.'
        : 'The approved operation is unresolved. Reload the case for its latest completion state.';
    case 'OPERATOR_REVIEW_REQUIRED':
      if (canCancel) {
        return 'This case requires review before execution. The API permits an accountable administrator cancellation.';
      }
      return canRetryCompletion
        ? 'The case remains locked for review. Retry completion reconciles the same approved operation.'
        : 'The case remains locked for review. Reload it to check whether same-operation completion retry is available.';
    case 'RESERVATION_RELEASE_PENDING':
      return 'A lifecycle decision is recorded, but protected cleanup is still releasing the proposed-phone reservation. This case is not terminal until the API reports its target terminal state.';
    case 'COMPLETED':
      return 'Recovery completed. The audit below records the outcome.';
    case 'REJECTED':
      return 'This recovery case was rejected. No further action is available in this console.';
    case 'CANCELLED':
      return 'This recovery case was cancelled. No further action is available in this console.';
    case 'FAILED':
      return canCancel
        ? 'This case failed before execution. Review its audit; the API permits an accountable administrator cancellation.'
        : 'This recovery case ended without completion. Review its audit and linked support information.';
    case 'UNKNOWN':
      return 'This case record is malformed. Review the restricted diagnostics; no recovery action is available.';
  }
}

function recoveryReasonDescription(reason?: string, reservationReleasePending = false): string | undefined {
  if (reason === 'CLAIMANT_CANCELLED') {
    return reservationReleasePending
      ? 'The claimant cancelled this unexecuted request. Its proposed-phone reservation is being released.'
      : 'The claimant cancelled this request before execution and its proposed-phone reservation was released.';
  }
  if (reason === 'ADMIN_CANCELLED') {
    return reservationReleasePending
      ? 'A system super administrator cancelled this unexecuted request. Its proposed-phone reservation is being released.'
      : 'A system super administrator cancelled this request before execution and its proposed-phone reservation was released.';
  }
  if (reason === 'ADMIN_REJECTED') {
    return reservationReleasePending
      ? 'The rejection decision is recorded and its proposed-phone reservation is being released.'
      : 'This request was rejected and its proposed-phone reservation was released.';
  }
  if (reason === 'PROFILE_EMAIL_CHANGED') {
    return reservationReleasePending
      ? 'Profile email changed after this recovery evidence was collected. The cancellation decision is recorded and its proposed-phone reservation is being released.'
      : 'Profile email changed after this recovery evidence was collected. The undecided request was cancelled and its proposed-phone reservation was released.';
  }
  return undefined;
}

function reservationReleaseDecisionLabel(targetTerminalStatus?: string): string {
  if (targetTerminalStatus === 'REJECTED') return 'Rejection decision';
  if (targetTerminalStatus === 'CANCELLED') return 'Cancellation decision';
  return 'Lifecycle decision';
}

function reservationReleaseTerminalLabel(targetTerminalStatus?: string): string {
  if (targetTerminalStatus === 'REJECTED' || targetTerminalStatus === 'CANCELLED') return targetTerminalStatus;
  return 'its target terminal state';
}

function auditEntryHasPendingReservationRelease(entry: { action: string; toStatus?: string }): boolean {
  return entry.toStatus === 'RESERVATION_RELEASE_PENDING' || entry.action.includes('RESERVATION_RELEASE_PENDING');
}

function proofState(timestamp?: number): string {
  return timestamp ? `Completed ${formatUnixDateTimeCompact(timestamp)}` : 'Not completed';
}

function conflictMessage(error: unknown): boolean {
  return Boolean(
    error && typeof error === 'object' && 'status' in error && (error as { status?: number }).status === 409,
  );
}

export default function RecoveryCaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();
  const recoveryCase = useAppSelector(selectSelectedRecoveryCase);
  const evidence = useAppSelector(selectSelectedRecoveryEvidence);
  const isLoading = useAppSelector(selectRecoveryCaseLoading);
  const error = useAppSelector(selectRecoveryCaseError);
  const isSubmittingDecision = useAppSelector(selectRecoveryDecisionSubmitting);
  const isRevokingApproval = useAppSelector(selectRecoveryApprovalRevoking);
  const isCancelling = useAppSelector(selectRecoveryCancelling);
  const isExecuting = useAppSelector(selectRecoveryExecuting);
  const executionFeedback = useAppSelector(selectRecoveryExecutionFeedback);
  const homeownerIdentity = useAppSelector(selectExactHomeownerPoolUser);
  const homeownerIdentityLoading = useAppSelector(selectHomeownerPoolLoading);
  const homeownerIdentityError = useAppSelector(selectHomeownerPoolError);
  const homeownerIdentityErrorKind = useAppSelector(selectHomeownerPoolErrorKind);
  const [reason, setReason] = useState('');
  const [approvalRevocationMessage, setApprovalRevocationMessage] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [executionAcknowledged, setExecutionAcknowledged] = useState(false);
  const [identityDialogOpen, setIdentityDialogOpen] = useState(false);
  const caseRequestRef = useRef<{ abort: () => void } | null>(null);
  const identityRequestRef = useRef<{ abort: () => void } | null>(null);

  const currentCase = recoveryCase?.caseId === caseId ? recoveryCase : null;
  const reasonIsValid = reason.trim().length >= 3 && reason.trim().length <= 500;
  const approvalRevocationMessageIsValid =
    approvalRevocationMessage.trim().length >= 3 && approvalRevocationMessage.trim().length <= 500;
  const cancellationReasonIsValid = cancellationReason.trim().length >= 1 && cancellationReason.trim().length <= 500;
  const hasFullClaimantContacts = Boolean(
    evidence?.claimantContacts?.oldPrimaryPhone &&
      evidence.claimantContacts.recoveryEmail &&
      evidence.claimantContacts.newPrimaryPhone,
  );
  const audit = useMemo(
    () => [...(evidence?.audit ?? [])].sort((left, right) => left.timestamp - right.timestamp),
    [evidence],
  );
  const executionAttempts = useMemo(
    () =>
      [...(evidence?.execution.attempts ?? [])].sort(
        (left, right) => (left.attemptNumber ?? 0) - (right.attemptNumber ?? 0),
      ),
    [evidence],
  );
  const lastExecutionAttempt = executionAttempts.at(-1);
  const emailReceipt = evidence?.evidenceSnapshot.receipts.find(
    (receipt) => receipt.evidenceType === 'RECOVERY_EMAIL_CONTROL',
  );
  const phoneReceipt = evidence?.evidenceSnapshot.receipts.find(
    (receipt) => receipt.evidenceType === 'PROPOSED_PHONE_CONTROL',
  );
  const contextReceipt = evidence?.evidenceSnapshot.receipts.find(
    (receipt) => receipt.evidenceType === 'ACCOUNT_CONTEXT_STATEMENT',
  );
  const isLoadingEvidence = isLoading;
  const contactEvidenceUnavailable = evidence?.claimantContactEvidence?.status === 'UNAVAILABLE';
  const integrity = evidence?.integrity ?? currentCase?.integrity;
  const caseIsMalformed = integrity?.status === 'MALFORMED';
  const operatorReviewFeedback = executionFeedback?.code === 'RECOVERY_EXECUTION_REQUIRES_OPERATOR_REVIEW';
  const retryAvailableFeedback = executionFeedback?.code === 'RECOVERY_EXECUTION_RETRY_AVAILABLE';
  const dependencyUnavailableFeedback = executionFeedback?.code === 'RECOVERY_AUTH_SERVICE_UNAVAILABLE';
  const lifecycleReason =
    evidence?.reservationRelease?.reason ??
    currentCase?.finalOutcome?.reason ??
    evidence?.execution.finalOutcome?.reason;
  const canRetryCompletion = Boolean(
    currentCase?.canRetryCompletion &&
      (currentCase.status === 'EXECUTING' || currentCase.status === 'OPERATOR_REVIEW_REQUIRED'),
  );
  const canCancel = Boolean(currentCase?.canCancel);
  const canRetryCancellation = Boolean(currentCase?.canRetryCancellation);
  const hasRestrictedDiagnostics = Boolean(
    currentCase?.failureCode ||
      lastExecutionAttempt?.failureCode ||
      evidence?.execution.reconciliation ||
      evidence?.diagnostics ||
      integrity,
  );

  const refresh = useCallback(async () => {
    if (!caseId) return;
    const request = dispatch(fetchRecoveryCase({ caseId }));
    caseRequestRef.current = request;
    try {
      await request.unwrap();
    } catch {
      // The Redux slice exposes the normalized secure-client error.
    } finally {
      if (caseRequestRef.current === request) caseRequestRef.current = null;
    }
  }, [caseId, dispatch]);

  useEffect(() => {
    void refresh();
    return () => {
      caseRequestRef.current?.abort();
      caseRequestRef.current = null;
      identityRequestRef.current?.abort();
      identityRequestRef.current = null;
      dispatch(clearHomeownerUsersPool());
      dispatch(clearSelectedRecoveryCase());
    };
  }, [dispatch, refresh]);

  const openAuthenticationIdentity = () => {
    const subject = evidence?.identityContinuity.before?.authIdentity?.subject;
    if (!subject) return;
    dispatch(clearHomeownerUsersPool());
    setIdentityDialogOpen(true);
    const request = dispatch(findHomeownerPoolUserBySub(subject));
    identityRequestRef.current = request;
    void request
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (identityRequestRef.current === request) identityRequestRef.current = null;
      });
  };

  const setAuthenticationIdentityDialogOpen = (open: boolean) => {
    setIdentityDialogOpen(open);
    if (!open) {
      identityRequestRef.current?.abort();
      identityRequestRef.current = null;
      dispatch(clearHomeownerUsersPool());
    }
  };

  const handleDecision = async (decision: 'approve' | 'reject') => {
    if (!currentCase || !evidence || !reasonIsValid || isSubmittingDecision || isCancelling) return;

    const approved = decision === 'approve';
    if (approved && !hasFullClaimantContacts) return;
    const confirmed = await confirm({
      title: approved ? 'Approve this recovery case?' : 'Reject this recovery case?',
      description: approved
        ? 'Approval does not complete recovery. You must separately execute recovery after reviewing the current case.'
        : 'Rejecting this case ends the current recovery request. Confirm that the supplied HomeVein account context is insufficient.',
      confirmLabel: approved ? 'Approve case' : 'Reject case',
      variant: approved ? 'default' : 'destructive',
    });
    if (!confirmed) return;

    try {
      const action = approved ? approveRecoveryCase : rejectRecoveryCase;
      await dispatch(
        action({
          caseId: currentCase.caseId,
          expectedVersion: currentCase.version,
          reason: reason.trim(),
          contextReview: approved ? 'CONSISTENT' : 'INSUFFICIENT',
        }),
      ).unwrap();
      setReason('');
      toast.success(
        approved ? 'Recovery case approved. Execute recovery remains a separate action.' : 'Recovery case rejected.',
      );
      await refresh();
    } catch (actionError) {
      if (conflictMessage(actionError)) {
        await refresh();
        toast.error('The case changed and was reloaded. Review the current case before taking another action.');
      } else {
        toast.error('The recovery case could not be updated. Please refresh and try again.');
      }
    }
  };

  const handleApprovalRevocation = async () => {
    if (!currentCase || !evidence || !approvalRevocationMessageIsValid || isRevokingApproval || isCancelling) return;

    const confirmed = await confirm({
      title: 'Revoke this approval?',
      description:
        'This returns the case to pending review so the claimant can update their submitted evidence. Recovery execution will not start.',
      confirmLabel: 'Revoke approval',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await dispatch(
        revokeRecoveryCaseApproval({
          caseId: currentCase.caseId,
          expectedVersion: currentCase.version,
          message: approvalRevocationMessage.trim(),
        }),
      ).unwrap();
      setApprovalRevocationMessage('');
      setExecutionAcknowledged(false);
      toast.success('Approval revoked. The case returned to pending review.');
      await refresh();
    } catch (actionError) {
      if (conflictMessage(actionError)) {
        await refresh();
        toast.error('The case changed and was reloaded. Review the current case before taking another action.');
      } else {
        toast.error('Approval could not be revoked. Please refresh and try again.');
      }
    }
  };

  const handleCancellation = async () => {
    if (!currentCase || !evidence || isCancelling) return;
    let cancellationCase = currentCase;
    let retrying = canRetryCancellation;

    if (retrying) {
      try {
        const reloaded = await dispatch(fetchRecoveryCase({ caseId: currentCase.caseId })).unwrap();
        cancellationCase = reloaded.summary;
      } catch {
        toast.error('The latest case version could not be loaded. No cancellation retry was sent.');
        return;
      }
      retrying = cancellationCase.canRetryCancellation;
      if (!retrying) {
        toast.info('Cancellation retry is not currently available. Review the freshly loaded case state.');
        return;
      }
    } else if (!canCancel || !cancellationReasonIsValid) {
      return;
    }

    const immutableReason = retrying ? cancellationCase.cancellation?.reason : cancellationReason.trim();
    if (!immutableReason) {
      toast.error('The immutable cancellation reason is unavailable. No retry was sent.');
      return;
    }

    const confirmed = await confirm({
      title: retrying ? 'Retry reservation release?' : 'Cancel this recovery request?',
      description: retrying
        ? 'This retries the exact recorded cancellation. It cannot change the reason, evidence, account, or proposed phone.'
        : 'HomeVein will stop this unexecuted request and release its proposed phone. The current homeowner account will not be changed.',
      confirmLabel: retrying ? 'Retry release' : 'Cancel recovery',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      const result = await dispatch(
        cancelRecoveryCase({
          caseId: cancellationCase.caseId,
          expectedVersion: cancellationCase.version,
          reason: immutableReason,
        }),
      ).unwrap();
      setCancellationReason('');
      setExecutionAcknowledged(false);
      toast.success(
        result.status === 'CANCELLED'
          ? 'Recovery cancelled. The proposed-phone reservation was released.'
          : 'Cancellation is recorded. The proposed-phone reservation release is still pending.',
      );
      await refresh();
    } catch (actionError) {
      const actionCode =
        actionError && typeof actionError === 'object' && 'code' in actionError
          ? (actionError as { code?: string }).code
          : undefined;
      await refresh();
      if (actionCode === 'RECOVERY_EXECUTION_WON_CONFLICT') {
        toast.error('Recovery execution has already started. Use the existing completion/reconciliation path.');
      } else if (actionCode === 'RECOVERY_RESERVATION_RELEASE_CONFLICT') {
        toast.error('The reservation could not be released safely. The case remains protected for operator review.');
      } else if (conflictMessage(actionError)) {
        toast.error('The case changed and was reloaded. Review it before taking another action.');
      } else {
        toast.error('Cancellation could not be confirmed. The case and phone reservation remain protected.');
      }
    }
  };

  const handleExecution = async () => {
    if (!currentCase || !evidence || !executionAcknowledged || isExecuting || isCancelling) return;
    if (currentCase.status !== 'APPROVED' && !canRetryCompletion) return;
    const isExecutionRetry = canRetryCompletion;
    let executionCase = currentCase;

    if (isExecutionRetry) {
      try {
        const reloaded = await dispatch(fetchRecoveryCase({ caseId: currentCase.caseId })).unwrap();
        executionCase = reloaded.summary;
      } catch {
        toast.error('The latest case version could not be loaded. No completion retry was sent.');
        return;
      }
      if (executionCase.status === 'COMPLETED') {
        setExecutionAcknowledged(false);
        toast.info('Recovery is already completed. The final case snapshot has been loaded.');
        return;
      }
      if (
        !executionCase.canRetryCompletion ||
        (executionCase.status !== 'EXECUTING' && executionCase.status !== 'OPERATOR_REVIEW_REQUIRED')
      ) {
        setExecutionAcknowledged(false);
        toast.info('Completion retry is not currently available. Review the freshly loaded case state.');
        return;
      }
    }

    const confirmed = await confirm({
      title: isExecutionRetry ? 'Retry completion?' : 'Execute recovery?',
      description: isExecutionRetry
        ? 'This asks the API to reconcile and complete the existing approved operation. It does not create a new operation or change the frozen evidence.'
        : 'I understand this starts the phone and authentication recovery process for this account.',
      confirmLabel: isExecutionRetry ? 'Retry completion' : 'Execute recovery',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      const result = await dispatch(
        executeRecoveryCase({ caseId: executionCase.caseId, expectedVersion: executionCase.version }),
      ).unwrap();
      setExecutionAcknowledged(false);
      toast.success(
        result.status === 'COMPLETED'
          ? 'Recovery completed. The final restricted snapshot and audit have been reloaded.'
          : isExecutionRetry
            ? 'The completion request was accepted for the existing operation. Reloaded state remains authoritative.'
            : 'Recovery execution was accepted. Reloaded state remains authoritative.',
      );
      await refresh();
    } catch (actionError) {
      const actionCode =
        actionError && typeof actionError === 'object' && 'code' in actionError
          ? (actionError as { code?: string }).code
          : undefined;
      if (actionCode === 'RECOVERY_EXECUTION_RETRY_AVAILABLE') {
        setExecutionAcknowledged(false);
        await refresh();
        toast.info('Auth did not complete the approved operation. Review the reloaded case and retry completion.');
        return;
      }
      if (actionCode === 'RECOVERY_EXECUTION_REQUIRES_OPERATOR_REVIEW') {
        setExecutionAcknowledged(false);
        await refresh();
        toast.error(
          'The same approved operation requires review. The case remains locked; use Retry completion when the reloaded case allows it.',
        );
        return;
      }
      if (actionCode === 'RECOVERY_AUTH_SERVICE_UNAVAILABLE') {
        setExecutionAcknowledged(false);
        await refresh();
        toast.error(
          'The dependency did not confirm completion. The case remains locked; review it and retry the same completion when available.',
        );
        return;
      }
      if (conflictMessage(actionError)) {
        await refresh();
        toast.error('The case changed and was reloaded. Review the current case before taking another action.');
      } else {
        toast.error('Recovery completion could not be confirmed. No competing operation was created.');
      }
    }
  };

  if (isLoading && !currentCase) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RouteBreadcrumb
        items={[{ label: 'Account recovery cases', to: '/admin/recovery-cases' }, { label: caseId ?? 'Case' }]}
      />

      <PageHeader
        title={currentCase ? `Recovery case ${currentCase.caseId}` : 'Account recovery case'}
        description={
          currentCase
            ? statusDescription(currentCase.status, currentCase.canRetryCompletion, currentCase.canCancel)
            : 'Recovery case details'
        }
        right={
          <Button type="button" variant="outline" onClick={() => void refresh()} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload case
          </Button>
        }
      />

      <ErrorNotice message={error} title="Recovery case unavailable" />

      {!currentCase ? null : (
        <>
          <Card
            className={
              currentCase.status === 'OPERATOR_REVIEW_REQUIRED' || operatorReviewFeedback
                ? 'border-destructive/50'
                : undefined
            }
          >
            <CardHeader>
              <div className="flex flex-wrap items-center gap-3">
                <CardTitle>Case status</CardTitle>
                <Badge variant={STATUS_VARIANTS[currentCase.status]}>{currentCase.status}</Badge>
              </div>
              <CardDescription>
                {statusDescription(currentCase.status, currentCase.canRetryCompletion, currentCase.canCancel)}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-4">
              <div>
                <span className="text-muted-foreground block">Requested</span>
                {currentCase.requestedAt ? formatUnixDateTimeCompact(currentCase.requestedAt) : 'Not available'}
              </div>
              <div>
                <span className="text-muted-foreground block">Last updated</span>
                {currentCase.updatedAt ? formatUnixDateTimeCompact(currentCase.updatedAt) : 'Not available'}
              </div>
              <div>
                <span className="text-muted-foreground block">Masked new phone</span>
                {currentCase.newPrimaryPhoneHint ?? 'Not available'}
              </div>
              <div>
                <span className="text-muted-foreground block">Case version</span>
                {currentCase.version || 'Not available'}
              </div>
            </CardContent>
          </Card>

          {retryAvailableFeedback || dependencyUnavailableFeedback ? (
            <Card className="border-amber-500/60 bg-amber-500/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-amber-500" />
                  <CardTitle>Approved completion remains unresolved</CardTitle>
                </div>
                <CardDescription>
                  {dependencyUnavailableFeedback
                    ? 'The dependency did not confirm completion. Reload the locked case and retry the same approved operation when Retry completion is available.'
                    : 'Auth did not complete the approved operation. Reload the case, review its latest checkpoints, and use Retry completion for that same operation.'}
                </CardDescription>
              </CardHeader>
            </Card>
          ) : null}

          {currentCase.status === 'RESERVATION_RELEASE_PENDING' ? (
            <Card className="border-amber-500/60 bg-amber-500/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-amber-500" />
                  <CardTitle>Protected cleanup in progress</CardTitle>
                </div>
                <CardDescription>
                  {recoveryReasonDescription(evidence?.reservationRelease?.reason, true) ??
                    `The API recorded the ${reservationReleaseDecisionLabel(evidence?.reservationRelease?.targetTerminalStatus).toLowerCase()} and is releasing the proposed-phone reservation. Approval, rejection, execution, and completion retry are unavailable while cleanup remains pending.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <p>
                  <span className="text-muted-foreground">
                    {reservationReleaseDecisionLabel(evidence?.reservationRelease?.targetTerminalStatus)}:{' '}
                  </span>
                  recorded
                </p>
                <p>
                  <span className="text-muted-foreground">Auth reservation cleanup: </span>
                  {evidence?.reservationRelease?.status === 'RELEASED'
                    ? 'released; awaiting API terminalization'
                    : 'pending'}
                </p>
                <p className="text-muted-foreground">
                  This case is not terminal until the API reports{' '}
                  {reservationReleaseTerminalLabel(evidence?.reservationRelease?.targetTerminalStatus)}.
                </p>
              </CardContent>
            </Card>
          ) : null}

          {currentCase.status !== 'RESERVATION_RELEASE_PENDING' && recoveryReasonDescription(lifecycleReason) ? (
            <Card className="border-sky-500/50 bg-sky-500/5">
              <CardHeader>
                <CardTitle>Server-owned lifecycle cancellation</CardTitle>
                <CardDescription>{recoveryReasonDescription(lifecycleReason)}</CardDescription>
              </CardHeader>
            </Card>
          ) : null}

          {contactEvidenceUnavailable ? (
            <Card className="border-amber-500/60 bg-amber-500/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-amber-500" />
                  <CardTitle>Claimant contact evidence unavailable</CardTitle>
                </div>
                <CardDescription>
                  The case remains available for review, but its validated contact evidence cannot currently be shown.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                <span className="text-muted-foreground">Reason: </span>
                <span className="font-medium">
                  {evidence.claimantContactEvidence?.reason
                    ? toLabel(evidence.claimantContactEvidence.reason)
                    : 'Not provided'}
                </span>
              </CardContent>
            </Card>
          ) : null}

          <Card className="border-sky-500/50 bg-sky-500/5">
            <CardHeader>
              <CardTitle>Primary recovery decision factors</CardTitle>
              <CardDescription>
                {contactEvidenceUnavailable
                  ? 'Validated claimant contact values are unavailable for this case. Other case evidence and diagnostics remain visible.'
                  : 'These claimant contact values come from the live, strongly validated case detail and must be reviewed together before approval.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="bg-background/70 rounded-lg border border-sky-500/30 p-4">
                <span className="text-muted-foreground block text-xs font-medium tracking-wide uppercase">
                  Claimant old phone number
                </span>
                <span className="mt-2 block font-mono text-sm font-semibold break-all">
                  {evidence?.claimantContacts?.oldPrimaryPhone ?? 'Not supplied by API'}
                </span>
              </div>
              <div className="bg-background/70 rounded-lg border border-sky-500/30 p-4">
                <span className="text-muted-foreground block text-xs font-medium tracking-wide uppercase">
                  Recovery email address
                </span>
                <span className="mt-2 block font-mono text-sm font-semibold break-all">
                  {evidence?.claimantContacts?.recoveryEmail ?? 'Not supplied by API'}
                </span>
              </div>
              <div className="bg-background/70 rounded-lg border border-sky-500/30 p-4">
                <span className="text-muted-foreground block text-xs font-medium tracking-wide uppercase">
                  New phone number
                </span>
                <span className="mt-2 block font-mono text-sm font-semibold break-all">
                  {evidence?.claimantContacts?.newPrimaryPhone ?? 'Not supplied by API'}
                </span>
              </div>
            </CardContent>
          </Card>

          {currentCase.status === 'OPERATOR_REVIEW_REQUIRED' || operatorReviewFeedback ? (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="text-destructive h-5 w-5" />
                  <CardTitle>Manual review required</CardTitle>
                </div>
                <CardDescription>
                  {canRetryCompletion
                    ? 'The case remains locked. Retry completion reconciles the same approved operation and cannot alter its phone, evidence, or frozen manifest.'
                    : canCancel
                      ? 'This review state has no execution operation. The API permits cancellation with an immutable administrator reason; it does not permit arbitrary repair or identity mutation.'
                      : 'The case remains protected while its state is reviewed. Reload it to check for the API-owned Retry completion path; this console has no unlock, arbitrary mutation, deletion, or generic repair control.'}
                </CardDescription>
              </CardHeader>
              {operatorReviewFeedback ? (
                <CardContent className="space-y-2 text-sm">
                  {executionFeedback.recovery?.issue ? (
                    <p>
                      <span className="text-muted-foreground">Issue: </span>
                      {toLabel(executionFeedback.recovery.issue)}
                    </p>
                  ) : null}
                  {executionFeedback.recovery?.requiredAction ? (
                    <p>
                      <span className="text-muted-foreground">Required action: </span>
                      {toLabel(executionFeedback.recovery.requiredAction)}
                    </p>
                  ) : null}
                </CardContent>
              ) : null}
            </Card>
          ) : null}

          {hasRestrictedDiagnostics ? (
            <Card className={integrity?.status === 'MALFORMED' ? 'border-destructive/60' : undefined}>
              <CardHeader>
                <CardTitle>Restricted recovery diagnostics</CardTitle>
                <CardDescription>
                  Live operator-only recovery state. This information is not copied to Support or telemetry.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {integrity ? (
                  <div className="rounded-md border p-3">
                    <p className="font-medium">Record integrity: {integrity.status ?? 'Not available'}</p>
                    {integrity.issues.length ? (
                      <p className="text-muted-foreground mt-1">Issues: {integrity.issues.map(toLabel).join(', ')}</p>
                    ) : null}
                  </div>
                ) : null}
                {evidence?.diagnostics ? (
                  <div className="rounded-md border p-3">
                    <p className="font-medium">Detail diagnostics: {evidence.diagnostics.status ?? 'Not available'}</p>
                    {evidence.diagnostics.issues.length ? (
                      <p className="text-muted-foreground mt-1">
                        Issues: {evidence.diagnostics.issues.map(toLabel).join(', ')}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {currentCase.failureCode || lastExecutionAttempt?.failureCode ? (
                  <div className="rounded-md border p-3">
                    <p className="font-medium">Last execution failure</p>
                    <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                      <div>
                        <dt className="text-muted-foreground">Code</dt>
                        <dd>{toLabel(lastExecutionAttempt?.failureCode ?? currentCase.failureCode ?? '')}</dd>
                      </div>
                      {(lastExecutionAttempt?.completedAt ?? lastExecutionAttempt?.attemptedAt) ? (
                        <div>
                          <dt className="text-muted-foreground">Occurred</dt>
                          <dd>
                            {formatUnixDateTimeCompact(
                              lastExecutionAttempt.completedAt ?? lastExecutionAttempt.attemptedAt ?? 0,
                            )}
                          </dd>
                        </div>
                      ) : null}
                      {lastExecutionAttempt?.outcome ? (
                        <div>
                          <dt className="text-muted-foreground">Attempt outcome</dt>
                          <dd>{toLabel(lastExecutionAttempt.outcome)}</dd>
                        </div>
                      ) : null}
                      {lastExecutionAttempt?.authMutationOccurred !== undefined ? (
                        <div>
                          <dt className="text-muted-foreground">Account authentication changed</dt>
                          <dd>{lastExecutionAttempt.authMutationOccurred ? 'Yes' : 'No'}</dd>
                        </div>
                      ) : null}
                      {lastExecutionAttempt?.operationId ? (
                        <div className="sm:col-span-2">
                          <dt className="text-muted-foreground">Operation ID</dt>
                          <dd className="font-mono text-xs break-all">{lastExecutionAttempt.operationId}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                ) : null}
                {evidence?.execution.reconciliation ? (
                  <div className="rounded-md border p-3">
                    <p className="font-medium">
                      Reconciliation: {toLabel(evidence.execution.reconciliation.status ?? 'RECORDED')}
                    </p>
                    {evidence.execution.reconciliation.reason ? (
                      <p className="text-muted-foreground mt-1">
                        Reason: {toLabel(evidence.execution.reconciliation.reason)}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Claimed membership and integrity</CardTitle>
              <CardDescription>
                Inspect the original account and its claimed HomeVein relationships without changing membership,
                ownership, roles, or history.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 xl:grid-cols-3">
              <div className="flex flex-col rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <Fingerprint className="text-muted-foreground h-5 w-5" />
                  <h3 className="font-semibold">Original authentication identity</h3>
                </div>
                <dl className="mt-4 space-y-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Issuer</dt>
                    <dd className="mt-1 font-mono text-xs break-all">
                      {evidence?.identityContinuity.before?.authIdentity?.issuer ?? 'Not supplied by API'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Subject</dt>
                    <dd className="mt-1 font-mono text-xs break-all">
                      {evidence?.identityContinuity.before?.authIdentity?.subject ?? 'Not supplied by API'}
                    </dd>
                  </div>
                </dl>
                <div className="mt-auto pt-4">
                  {evidence?.identityContinuity.before?.authIdentity?.subject ? (
                    <Button type="button" variant="outline" className="w-full" onClick={openAuthenticationIdentity}>
                      Open authentication identity
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" className="w-full" disabled>
                      Authentication identity unavailable
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-col rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <UserRound className="text-muted-foreground h-5 w-5" />
                  <h3 className="font-semibold">Original user profile</h3>
                </div>
                <dl className="mt-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Application user ID</dt>
                    <dd className="mt-1 font-mono text-xs break-all">
                      {evidence?.applicationUserId ?? 'Not supplied by API'}
                    </dd>
                  </div>
                </dl>
                <div className="mt-auto pt-4">
                  {evidence?.applicationUserId ? (
                    <Button type="button" variant="outline" className="w-full" asChild>
                      <Link
                        to={`/admin/homeowners/${encodeURIComponent(evidence.applicationUserId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open user profile
                        <span className="sr-only"> (opens in a new tab)</span>
                      </Link>
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" className="w-full" disabled>
                      User profile unavailable
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-col rounded-lg border p-4">
                <div className="flex items-center gap-2">
                  <Home className="text-muted-foreground h-5 w-5" />
                  <h3 className="font-semibold">
                    {evidence?.applicationUserId ? (
                      <Link
                        to={`/properties?applicationUserId=${encodeURIComponent(evidence.applicationUserId)}`}
                        className="underline-offset-4 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Property memberships (
                        {evidence.relationships.propertyMembershipCount ?? currentCase.propertyMembershipCount ?? 0})
                        <span className="sr-only"> (opens in a new tab)</span>
                      </Link>
                    ) : (
                      `Property memberships (${evidence?.relationships.propertyMembershipCount ?? currentCase.propertyMembershipCount ?? 0})`
                    )}
                  </h3>
                </div>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Organization memberships</dt>
                    <dd>
                      {evidence?.relationships.organizationMembershipCount ??
                        currentCase.organizationMembershipCount ??
                        'Not available'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Unchanged since approval</dt>
                    <dd>
                      {evidence?.relationships.unchangedSinceApproval === null ||
                      evidence?.relationships.unchangedSinceApproval === undefined
                        ? 'Not available'
                        : evidence.relationships.unchangedSinceApproval
                          ? 'Yes'
                          : 'No'}
                    </dd>
                  </div>
                </dl>
                {evidence?.relationships.authorityWorkflowConflicts.length ? (
                  <div className="border-destructive/40 bg-destructive/5 text-destructive mt-4 rounded-md border p-3 text-sm">
                    Authority workflow conflicts:{' '}
                    {evidence.relationships.authorityWorkflowConflicts.map(toLabel).join(', ')}
                  </div>
                ) : null}
                <div className="mt-auto pt-4">
                  {evidence?.applicationUserId ? (
                    <Button type="button" variant="outline" className="w-full" asChild>
                      <Link
                        to={`/properties?applicationUserId=${encodeURIComponent(evidence.applicationUserId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open claimed properties
                        <span className="sr-only"> (opens in a new tab)</span>
                      </Link>
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" className="w-full" disabled>
                      Claimed properties unavailable
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Submitted evidence</CardTitle>
                <CardDescription>
                  This review supports HomeVein account continuity; it does not verify legal identity or property
                  ownership.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {[
                  ['Existing verified email', emailReceipt, currentCase.proofs.existingEmailVerifiedAt],
                  ['New phone control', phoneReceipt, currentCase.proofs.newPhoneVerifiedAt],
                  ['HomeVein context statement', contextReceipt, currentCase.proofs.contextSubmittedAt],
                ].map(([label, receipt, fallbackTime]) => {
                  const typedReceipt = typeof receipt === 'object' ? receipt : undefined;
                  const timestamp =
                    typedReceipt?.recordedAt ?? (typeof fallbackTime === 'number' ? fallbackTime : undefined);
                  return (
                    <div key={String(label)} className="rounded-md border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">{String(label)}</span>
                        <Badge variant="outline">{typedReceipt?.status ?? 'Not available'}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1">{proofState(timestamp)}</p>
                      {typedReceipt?.issuerRef ? (
                        <p className="text-muted-foreground">Authority: {toLabel(typedReceipt.issuerRef)}</p>
                      ) : null}
                      {typedReceipt?.evidenceId ? (
                        <p className="text-muted-foreground mt-1 font-mono text-xs break-all">
                          Evidence ID: {typedReceipt.evidenceId}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Full identity and clearance evidence</CardTitle>
                <CardDescription>
                  Visible only in this live system-super-admin view and cleared when leaving it.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <span>Application user ID</span>
                  <span className="text-right break-all">{evidence?.applicationUserId ?? 'Not supplied by API'}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Current identity issuer</span>
                  <span className="text-right break-all">
                    {evidence?.identityContinuity.before?.authIdentity?.issuer ?? 'Not supplied by API'}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Current identity subject</span>
                  <span className="text-right break-all">
                    {evidence?.identityContinuity.before?.authIdentity?.subject ?? 'Not supplied by API'}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Replacement identity subject</span>
                  <span className="text-right break-all">
                    {evidence?.displacedIdentity?.subject ?? 'No replacement identity'}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Replacement identity clearance</span>
                  <span>
                    {evidence?.replacementClearance
                      ? evidence.replacementClearance.reclaimable
                        ? 'Clear'
                        : 'Conflict'
                      : 'Not applicable'}
                  </span>
                </div>
                {Object.entries(evidence?.replacementClearance?.checks ?? {}).map(([check, present]) => (
                  <div key={check} className="flex justify-between gap-4">
                    <span>{toLabel(check)}</span>
                    <span>{present ? 'Application relationship found' : 'No application relationship found'}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Decision and frozen approval</CardTitle>
              <CardDescription>
                The decision and manifest apply only to this exact case version, evidence set, account, and phone.
                Completion retry cannot edit them.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 text-sm">
              <div className="grid gap-3 rounded-md border p-4 sm:grid-cols-2">
                <div>
                  <span className="text-muted-foreground block">Protocol</span>
                  <span>
                    {currentCase.protocolId ?? 'Not supplied'} / version {currentCase.protocolVersion ?? '—'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Policy and allowed action</span>
                  <span>
                    {currentCase.policyId ?? 'Not supplied'} /{' '}
                    {currentCase.allowedAction ? toLabel(currentCase.allowedAction) : 'Not supplied'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Recovery notice acknowledged</span>
                  <span>
                    {evidence?.recoveryNoticeAcceptedAt
                      ? formatUnixDateTimeCompact(evidence.recoveryNoticeAcceptedAt)
                      : 'Not supplied'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Account locator</span>
                  <span>
                    {evidence?.evidenceSnapshot.locator?.type
                      ? `${toLabel(evidence.evidenceSnapshot.locator.type)} / version ${evidence.evidenceSnapshot.locator.version ?? '—'}`
                      : 'Not supplied'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Profile email originally verified</span>
                  <span>
                    {evidence?.evidenceSnapshot.verifiedProfileEmailVerifiedAt
                      ? formatUnixDateTimeCompact(evidence.evidenceSnapshot.verifiedProfileEmailVerifiedAt)
                      : 'Not supplied'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Decision</span>
                  <span>
                    {currentCase.decision?.decision
                      ? `${toLabel(currentCase.decision.decision)}${currentCase.decision.revokedAt ? ' (revoked)' : ''}`
                      : 'Not decided'}
                  </span>
                </div>
                {currentCase.decision?.reviewer ? (
                  <div>
                    <span className="text-muted-foreground block">Recovery Officer</span>
                    <span>{currentCase.decision.reviewer.displayName ?? 'Name not supplied'}</span>
                    {currentCase.decision.reviewer.applicationUserId ? (
                      <span className="block font-mono text-xs break-all">
                        {currentCase.decision.reviewer.applicationUserId}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {currentCase.decision?.decidedAt ? (
                  <div>
                    <span className="text-muted-foreground block">Decision time / reviewed case version</span>
                    <span>
                      {formatUnixDateTimeCompact(currentCase.decision.decidedAt)} /{' '}
                      {currentCase.decision.caseVersion ?? '—'}
                    </span>
                  </div>
                ) : null}
                {currentCase.decision?.reason ? (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground block">Decision reason</span>
                    <span className="whitespace-pre-wrap">{currentCase.decision.reason}</span>
                  </div>
                ) : null}
                {currentCase.decision?.revokedAt ? (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground block">Approval revocation</span>
                    <span>
                      {formatUnixDateTimeCompact(currentCase.decision.revokedAt)} by{' '}
                      {currentCase.decision.revokedBy?.displayName ??
                        currentCase.decision.revokedBy?.applicationUserId ??
                        'system super administrator'}
                    </span>
                    {currentCase.decision.revocationReason ? (
                      <span className="mt-1 block whitespace-pre-wrap">
                        Reason: {currentCase.decision.revocationReason}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {evidence?.approvedManifest ? (
                <div className="space-y-3 rounded-md border p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <span className="text-muted-foreground block">Frozen manifest ID</span>
                      <span className="font-mono text-xs break-all">
                        {evidence.approvedManifest.manifestId ?? 'Not supplied'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">Frozen at / case version</span>
                      <span>
                        {evidence.approvedManifest.frozenAt
                          ? formatUnixDateTimeCompact(evidence.approvedManifest.frozenAt)
                          : 'Not supplied'}{' '}
                        / {evidence.approvedManifest.caseVersion ?? '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">Target application user ID</span>
                      <span className="font-mono text-xs break-all">
                        {evidence.approvedManifest.targetApplicationUserId ?? 'Not supplied'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">Authority snapshot</span>
                      <span className="font-mono text-xs break-all">
                        {evidence.approvedManifest.authoritySnapshotRef ?? 'Not supplied'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium">Frozen evidence references</p>
                    {evidence.approvedManifest.evidenceReferences.map((reference, index) => (
                      <div key={`${reference.evidenceId ?? 'evidence'}-${index}`} className="rounded border p-3">
                        <p>{reference.evidenceType ? toLabel(reference.evidenceType) : 'Evidence reference'}</p>
                        <p className="text-muted-foreground font-mono text-xs break-all">
                          Evidence ID: {reference.evidenceId ?? 'Not supplied'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No approved manifest exists for the current case state.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Claimant HomeVein context</CardTitle>
              <CardDescription>Limited account context supplied for this recovery review.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {evidence?.evidenceSnapshot.contextStatements.length ? (
                evidence.evidenceSnapshot.contextStatements.map((answer) => (
                  <div key={answer.promptKey} className="rounded-md border p-3 text-sm">
                    <p className="text-muted-foreground font-medium">{toLabel(answer.promptKey)}</p>
                    <p className="mt-1 whitespace-pre-wrap">{answer.answer}</p>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No claimant context is available for this case.</p>
              )}
            </CardContent>
          </Card>

          {currentCase.support?.ticketID ? (
            <Card>
              <CardHeader>
                <CardTitle>Linked support ticket</CardTitle>
                <CardDescription>
                  Support is a conversation record only; ticket actions cannot approve, reject, or execute recovery.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button type="button" variant="outline" asChild>
                  <Link
                    to={`/admin/support/${encodeURIComponent(currentCase.support.ticketID)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open linked support ticket
                    <span className="sr-only"> (opens in a new tab)</span>
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {currentCase.status === 'PENDING_REVIEW' && !caseIsMalformed ? (
            <Card>
              <CardHeader>
                <CardTitle>Review decision</CardTitle>
                <CardDescription>
                  Provide a 3–500 character reason. Approval and execution are intentionally separate actions.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recovery-decision-reason">Decision reason</Label>
                  <Textarea
                    id="recovery-decision-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    maxLength={500}
                    placeholder="Describe why the HomeVein recovery context is consistent or insufficient."
                  />
                  <p className="text-muted-foreground text-xs">{reason.trim().length}/500 characters</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={() => void handleDecision('approve')}
                    disabled={
                      !evidence ||
                      !hasFullClaimantContacts ||
                      isLoadingEvidence ||
                      !reasonIsValid ||
                      isSubmittingDecision ||
                      isCancelling
                    }
                  >
                    Approve as consistent
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => void handleDecision('reject')}
                    disabled={!evidence || isLoadingEvidence || !reasonIsValid || isSubmittingDecision || isCancelling}
                  >
                    Reject as insufficient
                  </Button>
                </div>
                {!hasFullClaimantContacts ? (
                  <p className="text-destructive text-sm">
                    Approval is unavailable until all three claimant contact values are returned as full evidence.
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {(canCancel || canRetryCancellation) && !caseIsMalformed ? (
            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle>{canRetryCancellation ? 'Retry reservation release' : 'Cancel recovery'}</CardTitle>
                <CardDescription>
                  {canRetryCancellation
                    ? 'Retry the exact recorded cancellation. The case remains active until Auth confirms that the proposed-phone reservation was released.'
                    : 'Cancel this request only before recovery execution begins. This releases the proposed phone without changing the current homeowner account.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {canRetryCancellation ? (
                  <div className="rounded-md border p-3 text-sm">
                    <span className="text-muted-foreground block">Immutable cancellation reason</span>
                    <span>{currentCase.cancellation?.reason ?? 'Not supplied by API'}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="recovery-cancellation-reason">Cancellation reason</Label>
                    <Textarea
                      id="recovery-cancellation-reason"
                      value={cancellationReason}
                      onChange={(event) => setCancellationReason(event.target.value)}
                      maxLength={500}
                      placeholder="Explain why this unexecuted recovery request should be cancelled."
                    />
                    <p className="text-muted-foreground text-xs">
                      {cancellationReason.trim().length}/500 characters. This becomes immutable recovery audit evidence.
                      Do not include codes, tokens, or secrets.
                    </p>
                  </div>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void handleCancellation()}
                  disabled={
                    !evidence ||
                    isLoadingEvidence ||
                    isCancelling ||
                    isSubmittingDecision ||
                    isExecuting ||
                    isRevokingApproval ||
                    (!canRetryCancellation && !cancellationReasonIsValid) ||
                    (canRetryCancellation && !currentCase.cancellation?.reason)
                  }
                >
                  {isCancelling
                    ? canRetryCancellation
                      ? 'Retrying release…'
                      : 'Cancelling recovery…'
                    : canRetryCancellation
                      ? 'Retry release'
                      : 'Cancel recovery'}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {currentCase.status === 'APPROVED' && !caseIsMalformed ? (
            <Card className="border-amber-500/40">
              <CardHeader>
                <CardTitle>Revoke approval</CardTitle>
                <CardDescription>
                  Return this case to pending review before execution so the claimant can correct their evidence.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recovery-approval-revocation-message">Administrative explanation</Label>
                  <Textarea
                    id="recovery-approval-revocation-message"
                    value={approvalRevocationMessage}
                    onChange={(event) => setApprovalRevocationMessage(event.target.value)}
                    maxLength={500}
                    placeholder="Explain why this approval must return to review."
                  />
                  <p className="text-muted-foreground text-xs">
                    {approvalRevocationMessage.trim().length}/500 characters. Audit-only: not shown to the claimant or
                    copied to Support. Do not include contact values, codes, or submitted answers.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleApprovalRevocation()}
                  disabled={
                    !evidence ||
                    isLoadingEvidence ||
                    !approvalRevocationMessageIsValid ||
                    isRevokingApproval ||
                    isExecuting
                  }
                >
                  {isRevokingApproval ? 'Revoking approval…' : 'Revoke approval'}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {(currentCase.status === 'APPROVED' || canRetryCompletion) && !caseIsMalformed ? (
            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle>{canRetryCompletion ? 'Retry completion' : 'Execute recovery'}</CardTitle>
                <CardDescription>
                  {canRetryCompletion
                    ? 'Web reloads the case before sending its latest expectedVersion. The API reuses the one stable operation and frozen manifest; success is not assumed until the case returns COMPLETED.'
                    : 'This is irreversible from the Web console and begins the separate phone and authentication recovery process.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 rounded-md border p-3">
                  <Checkbox
                    id="execute-recovery-acknowledgement"
                    checked={executionAcknowledged}
                    onCheckedChange={(checked) => setExecutionAcknowledged(checked === true)}
                  />
                  <Label htmlFor="execute-recovery-acknowledgement" className="leading-5">
                    {canRetryCompletion
                      ? 'I understand this retries completion of the existing approved operation without changing its frozen evidence.'
                      : 'I understand this starts the phone and authentication recovery process for this account.'}
                  </Label>
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void handleExecution()}
                  disabled={
                    !evidence ||
                    isLoadingEvidence ||
                    !executionAcknowledged ||
                    isExecuting ||
                    isRevokingApproval ||
                    isCancelling
                  }
                >
                  {isExecuting
                    ? canRetryCompletion
                      ? 'Retrying completion…'
                      : 'Starting execution…'
                    : canRetryCompletion
                      ? 'Retry completion'
                      : 'Execute recovery'}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {currentCase.status === 'COMPLETED' ? (
            <Card className="border-primary/40">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-primary h-5 w-5" />
                  <CardTitle>Recovery completed</CardTitle>
                </div>
                <CardDescription>
                  The same HomeVein account continuity was restored. No further action is available here.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Execution convergence and outcome</CardTitle>
              <CardDescription>
                One stable operation, its technical attempts, API/Auth checkpoints, reconciliation state, and
                before/after continuity.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {evidence?.execution.operationId ? (
                <p className="text-muted-foreground text-sm break-all">
                  Stable operation ID: <span className="font-mono text-xs">{evidence.execution.operationId}</span>
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">Execution has not created an operation ID.</p>
              )}

              <div className="grid gap-4 lg:grid-cols-2">
                {[
                  ['Before recovery', evidence?.identityContinuity.before],
                  ['After recovery', evidence?.identityContinuity.after],
                ].map(([label, snapshot]) => {
                  const continuity = typeof snapshot === 'object' ? snapshot : undefined;
                  return (
                    <div key={String(label)} className="rounded-md border p-4 text-sm">
                      <p className="font-medium">{String(label)}</p>
                      {continuity ? (
                        <dl className="mt-3 space-y-2">
                          <div>
                            <dt className="text-muted-foreground">Application user ID</dt>
                            <dd className="font-mono text-xs break-all">
                              {continuity.applicationUserId ?? 'Not supplied'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-muted-foreground">Authentication identity</dt>
                            <dd className="font-mono text-xs break-all">
                              {continuity.authIdentity?.issuer ?? 'Not supplied'} /{' '}
                              {continuity.authIdentity?.subject ?? 'Not supplied'}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt className="text-muted-foreground">Primary phone</dt>
                            <dd className="font-mono text-xs break-all">{continuity.primaryPhone ?? 'Not supplied'}</dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt className="text-muted-foreground">Profile status</dt>
                            <dd>{continuity.profileStatus ? toLabel(continuity.profileStatus) : 'Not supplied'}</dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-muted-foreground mt-3">Not recorded yet.</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3">
                <h3 className="font-medium">Execution checkpoints</h3>
                {Object.entries(evidence?.execution.steps ?? {}).length ? (
                  Object.entries(evidence?.execution.steps ?? {}).map(([step, timestamp]) => (
                    <div key={step} className="flex items-center justify-between gap-4 rounded-md border p-3 text-sm">
                      <span className="font-medium">{toLabel(step)}</span>
                      <span className="text-muted-foreground">{formatUnixDateTimeCompact(timestamp)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">No execution steps are available for this case yet.</p>
                )}
              </div>

              <div className="space-y-3">
                <h3 className="font-medium">Technical attempts and Auth outcomes</h3>
                {executionAttempts.length ? (
                  executionAttempts.map((attempt, index) => (
                    <div
                      key={`${attempt.operationId ?? 'attempt'}-${attempt.attemptNumber ?? index}`}
                      className="rounded-md border p-3 text-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-medium">Attempt {attempt.attemptNumber ?? index + 1}</p>
                        <Badge variant={attempt.failureCode ? 'destructive' : 'outline'}>
                          {attempt.outcome ? toLabel(attempt.outcome) : 'Recorded'}
                        </Badge>
                      </div>
                      {attempt.attemptedAt ? (
                        <p className="text-muted-foreground mt-1">
                          Started: {formatUnixDateTimeCompact(attempt.attemptedAt)}
                        </p>
                      ) : null}
                      {attempt.completedAt ? (
                        <p className="text-muted-foreground">
                          Recorded: {formatUnixDateTimeCompact(attempt.completedAt)}
                        </p>
                      ) : null}
                      {attempt.authOutcomeRecordedAt ? (
                        <p className="text-muted-foreground">
                          Auth outcome recorded: {formatUnixDateTimeCompact(attempt.authOutcomeRecordedAt)}
                        </p>
                      ) : null}
                      {attempt.failureCode ? (
                        <p className="text-muted-foreground">Failure: {toLabel(attempt.failureCode)}</p>
                      ) : null}
                      {attempt.operationId ? (
                        <p className="text-muted-foreground mt-1 font-mono text-xs break-all">
                          Operation ID: {attempt.operationId}
                        </p>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">No execution attempts are recorded.</p>
                )}
              </div>

              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <div className="rounded-md border p-3">
                  <p className="font-medium">Reconciliation</p>
                  <p className="text-muted-foreground mt-1">
                    {evidence?.execution.reconciliation?.status
                      ? toLabel(evidence.execution.reconciliation.status)
                      : 'Not required'}
                  </p>
                  {evidence?.execution.reconciliation?.reason ? (
                    <p className="text-muted-foreground">{toLabel(evidence.execution.reconciliation.reason)}</p>
                  ) : null}
                </div>
                <div className="rounded-md border p-3">
                  <p className="font-medium">Device cleanup</p>
                  <p className="text-muted-foreground mt-1">
                    {evidence?.execution.deviceCleanup?.completedAt
                      ? `${evidence.execution.deviceCleanup.deletedDeviceTokens ?? 0} token(s) cleared at ${formatUnixDateTimeCompact(evidence.execution.deviceCleanup.completedAt)}`
                      : 'Not completed'}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="font-medium">Final outcome</p>
                  <p className="text-muted-foreground mt-1">
                    {evidence?.execution.finalOutcome?.outcome
                      ? toLabel(evidence.execution.finalOutcome.outcome)
                      : 'Not finalized'}
                  </p>
                  {evidence?.execution.finalOutcome?.recordedAt ? (
                    <p className="text-muted-foreground">
                      {formatUnixDateTimeCompact(evidence.execution.finalOutcome.recordedAt)}
                    </p>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Audit timeline</CardTitle>
              <CardDescription>
                Immutable recovery-case evidence returned for the live system-super-admin review.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {audit.length ? (
                audit.map((entry) => (
                  <div
                    key={entry.eventId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{toLabel(entry.action)}</p>
                      <p className="text-muted-foreground">
                        {entry.fromStatus
                          ? `${entry.fromStatus} → ${entry.toStatus ?? 'current state'}`
                          : entry.step
                            ? toLabel(entry.step)
                            : 'Case event'}
                      </p>
                      {entry.actor.type ? (
                        <p className="text-muted-foreground">
                          Actor: {entry.actor.displayName ?? toLabel(entry.actor.type)}
                        </p>
                      ) : null}
                      {entry.actor.applicationUserId ? (
                        <p className="text-muted-foreground break-all">
                          Actor application user ID: {entry.actor.applicationUserId}
                        </p>
                      ) : null}
                      {entry.operationId ? (
                        <p className="text-muted-foreground break-all">Operation ID: {entry.operationId}</p>
                      ) : null}
                      {recoveryReasonDescription(entry.reason, currentCase.status === 'RESERVATION_RELEASE_PENDING') ? (
                        <p className="text-muted-foreground">
                          {recoveryReasonDescription(entry.reason, auditEntryHasPendingReservationRelease(entry))}
                        </p>
                      ) : entry.reason ? (
                        <p className="text-muted-foreground">Reason: {entry.reason}</p>
                      ) : null}
                      {entry.before ? (
                        <details className="mt-2">
                          <summary className="text-muted-foreground cursor-pointer">Before snapshot</summary>
                          <pre className="bg-muted mt-1 overflow-x-auto rounded p-2 text-xs break-words whitespace-pre-wrap">
                            {JSON.stringify(entry.before, null, 2)}
                          </pre>
                        </details>
                      ) : null}
                      {entry.after ? (
                        <details className="mt-2">
                          <summary className="text-muted-foreground cursor-pointer">After snapshot</summary>
                          <pre className="bg-muted mt-1 overflow-x-auto rounded p-2 text-xs break-words whitespace-pre-wrap">
                            {JSON.stringify(entry.after, null, 2)}
                          </pre>
                        </details>
                      ) : null}
                      {Object.entries(entry.technicalIds ?? {}).map(([key, detail]) => (
                        <p key={key} className="text-muted-foreground break-all">
                          {toLabel(key)}: {String(detail)}
                        </p>
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={entry.outcome === 'FAILED' ? 'destructive' : 'outline'}>
                        {entry.outcome ?? 'RECORDED'}
                      </Badge>
                      <span className="text-muted-foreground">{formatUnixDateTimeCompact(entry.timestamp)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No audit entries are available.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={identityDialogOpen} onOpenChange={setAuthenticationIdentityDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Original authentication identity</DialogTitle>
            <DialogDescription>
              Live, read-only homeowner authentication metadata for this recovery review.
            </DialogDescription>
          </DialogHeader>

          {homeownerIdentityLoading ? (
            <div className="flex min-h-40 items-center justify-center gap-3">
              <LoadingSpinner size={32} />
              <p className="text-muted-foreground text-sm">Loading authentication identity…</p>
            </div>
          ) : homeownerIdentityError ? (
            <div className="space-y-4 rounded-lg border p-4">
              <p role="alert" className={homeownerIdentityErrorKind === 'access-denied' ? 'text-destructive' : ''}>
                {homeownerIdentityError}
              </p>
              {homeownerIdentityErrorKind === 'unavailable' ? (
                <Button type="button" variant="outline" onClick={openAuthenticationIdentity}>
                  Try again
                </Button>
              ) : null}
            </div>
          ) : homeownerIdentity ? (
            <HomeownerPoolDetails user={homeownerIdentity} />
          ) : (
            <p className="text-muted-foreground text-sm">No homeowner authentication identity is available.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
